import React, { useState } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const FilterControls = ({ 
  onFilterChange, 
  onSearchChange, 
  onClearFilters,
  totalResults = 0,
  filteredResults = 0 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    supplier: '',
    brand: '',
    matchType: '',
    confidenceRange: '',
    priceRange: '',
    stockStatus: ''
  });

  const supplierOptions = [
    { value: '', label: 'All Suppliers' },
    { value: 'techworld_supply', label: 'TechWorld Supply' },
    { value: 'global_electronics', label: 'Global Electronics' },
    { value: 'component_plus', label: 'Component Plus' },
    { value: 'digital_depot', label: 'Digital Depot' },
    { value: 'circuit_city_wholesale', label: 'Circuit City Wholesale' }
  ];

  const brandOptions = [
    { value: '', label: 'All Brands' },
    { value: 'apple', label: 'Apple' },
    { value: 'samsung', label: 'Samsung' },
    { value: 'sony', label: 'Sony' },
    { value: 'lg', label: 'LG' },
    { value: 'dell', label: 'Dell' },
    { value: 'hp', label: 'HP' },
    { value: 'lenovo', label: 'Lenovo' }
  ];

  const matchTypeOptions = [
    { value: '', label: 'All Match Types' },
    { value: 'exact_ean', label: 'Exact EAN Match' },
    { value: 'exact_mpn', label: 'Exact MPN Match' },
    { value: 'ai_fuzzy', label: 'AI Fuzzy Match' },
    { value: 'ambiguous', label: 'Needs Review' }
  ];

  const confidenceOptions = [
    { value: '', label: 'All Confidence Levels' },
    { value: '90-100', label: '90-100% (High)' },
    { value: '80-89', label: '80-89% (Medium)' },
    { value: '70-79', label: '70-79% (Low)' },
    { value: '0-69', label: 'Below 70% (Very Low)' }
  ];

  const priceRangeOptions = [
    { value: '', label: 'All Price Ranges' },
    { value: '0-50', label: '$0 - $50' },
    { value: '50-100', label: '$50 - $100' },
    { value: '100-500', label: '$100 - $500' },
    { value: '500-1000', label: '$500 - $1,000' },
    { value: '1000+', label: '$1,000+' }
  ];

  const stockStatusOptions = [
    { value: '', label: 'All Stock Status' },
    { value: 'in_stock', label: 'In Stock' },
    { value: 'low_stock', label: 'Low Stock' },
    { value: 'out_of_stock', label: 'Out of Stock' }
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    if (onFilterChange) onFilterChange(newFilters);
  };

  const handleSearchChange = (e) => {
    const value = e?.target?.value;
    handleFilterChange('search', value);
    if (onSearchChange) onSearchChange(value);
  };

  const handleClearAll = () => {
    const clearedFilters = {
      search: '',
      supplier: '',
      brand: '',
      matchType: '',
      confidenceRange: '',
      priceRange: '',
      stockStatus: ''
    };
    setFilters(clearedFilters);
    if (onFilterChange) onFilterChange(clearedFilters);
    if (onClearFilters) onClearFilters();
  };

  const getActiveFilterCount = () => {
    return Object.values(filters)?.filter(value => value !== '')?.length;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 mb-6">
      {/* Search and Toggle Row */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-4 flex-1">
          <div className="flex-1 max-w-md">
            <Input
              type="search"
              placeholder="Search products, brands, MPN, EAN..."
              value={filters?.search}
              onChange={handleSearchChange}
              className="w-full"
            />
          </div>
          
          <Button
            variant="outline"
            onClick={() => setIsExpanded(!isExpanded)}
            iconName={isExpanded ? 'ChevronUp' : 'ChevronDown'}
            iconPosition="right"
            className="whitespace-nowrap"
          >
            Filters {getActiveFilterCount() > 0 && `(${getActiveFilterCount()})`}
          </Button>
        </div>

        <div className="flex items-center space-x-4 ml-4">
          <div className="text-sm text-muted-foreground">
            Showing {filteredResults?.toLocaleString()} of {totalResults?.toLocaleString()} products
          </div>
          
          {getActiveFilterCount() > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAll}
              iconName="X"
              iconPosition="left"
              className="text-muted-foreground hover:text-foreground"
            >
              Clear All
            </Button>
          )}
        </div>
      </div>
      {/* Expanded Filters */}
      {isExpanded && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 pt-4 border-t border-border animate-fade-in">
          <Select
            label="Supplier"
            options={supplierOptions}
            value={filters?.supplier}
            onChange={(value) => handleFilterChange('supplier', value)}
          />
          
          <Select
            label="Brand"
            options={brandOptions}
            value={filters?.brand}
            onChange={(value) => handleFilterChange('brand', value)}
            searchable
          />
          
          <Select
            label="Match Type"
            options={matchTypeOptions}
            value={filters?.matchType}
            onChange={(value) => handleFilterChange('matchType', value)}
          />
          
          <Select
            label="Confidence Level"
            options={confidenceOptions}
            value={filters?.confidenceRange}
            onChange={(value) => handleFilterChange('confidenceRange', value)}
          />
          
          <Select
            label="Price Range"
            options={priceRangeOptions}
            value={filters?.priceRange}
            onChange={(value) => handleFilterChange('priceRange', value)}
          />
          
          <Select
            label="Stock Status"
            options={stockStatusOptions}
            value={filters?.stockStatus}
            onChange={(value) => handleFilterChange('stockStatus', value)}
          />
        </div>
      )}
    </div>
  );
};

export default FilterControls;